// Confirmation popup before restarting a user's profile submission deadline.
import React from 'react';
import { X, Hourglass, AlertCircle, Loader2 } from 'lucide-react';

interface ResetDeadlineModalProps {
  user: { firstName: string; lastName: string; email?: string | null };
  isSubmitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ResetDeadlineModal: React.FC<ResetDeadlineModalProps> = ({ user, isSubmitting, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 overflow-y-auto">
    <div className="flex min-h-full items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onCancel} />

      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
        <button onClick={onCancel} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X size={20} />
        </button>

        <div className="flex justify-center mb-4">
          <div className="p-3 bg-blue-100 rounded-full">
            <Hourglass size={32} className="text-blue-600" />
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Réinitialiser le délai de soumission</h3>

        <div className="bg-gray-50 rounded-lg p-4 mb-4 text-center">
          <p className="font-medium text-gray-900">
            {user.firstName} {user.lastName}
          </p>
          {user.email && <p className="text-sm text-gray-500">{user.email}</p>}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Un nouveau délai complet démarre aujourd'hui</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Le blocage « Délai dépassé » est levé immédiatement</li>
                <li>La durée appliquée est celle configurée pour l'établissement</li>
                <li>L'utilisateur doit se reconnecter pour voir le changement</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Réinitialisation...</span>
              </>
            ) : (
              <>
                <Hourglass size={16} />
                <span>Réinitialiser</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default ResetDeadlineModal;
